// Slice 5 — Pay run preview / DRAFT.
// Pick a pay calendar, pick (or default to) the most recently completed
// period, click "Create draft pay run" — Xero auto-generates payslips for
// every employee in that calendar based on the timesheets we pushed in
// Slice 4 plus their pay templates. Admin reviews and posts in Xero.

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api';

const BRISBANE_TZ = 'Australia/Brisbane';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};

const inputStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-background)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
};

const buttonPrimary: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  borderRadius: 'var(--radius-default)',
  border: 'none',
  backgroundColor: 'var(--color-accent)',
  color: '#1e1e1e',
  fontWeight: 600,
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '2px solid var(--color-border)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
};

const tdStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 'var(--font-size-sm)',
};

function brisbaneToday(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: BRISBANE_TZ });
}

function addDays(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function formatDate(d: string) {
  const dt = new Date(`${d}T12:00:00`);
  return dt.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: BRISBANE_TZ,
  });
}

function cadenceDays(calendarType: string): number {
  return calendarType === 'WEEKLY' ? 7 : calendarType === 'FORTNIGHTLY' ? 14 : 0;
}

/**
 * For a given calendar (anchor StartDate + cadence), return the period-end
 * date of the MOST-RECENTLY-CLOSED period. We default the picker to that —
 * admins almost always want to run payroll for the period that just finished.
 */
function lastClosedPeriodEnd(calStart: string, calendarType: string): string | null {
  const cadence = cadenceDays(calendarType);
  if (cadence === 0) return null;
  const start = new Date(`${calStart}T12:00:00Z`);
  const today = new Date(`${brisbaneToday()}T12:00:00Z`);
  const diffDays = Math.floor((today.getTime() - start.getTime()) / 86_400_000);
  // Walk back to the last period whose end date is <= today.
  const cycles = Math.floor(diffDays / cadence);
  const periodStart = addDays(calStart, cycles * cadence);
  const periodEnd = addDays(periodStart, cadence - 1);
  // If today IS the period-end, that period is closed (use it). Otherwise
  // the previous one is the most recent closed period.
  if (periodEnd <= brisbaneToday()) return periodEnd;
  return addDays(periodEnd, -cadence);
}

/**
 * Snap an arbitrary date onto the calendar's pay-period boundary — return
 * the period-end date for whichever period contains the input. Mirrors the
 * backend's alignToCalendar so the request always passes Xero's validation.
 */
function snapToPeriodEnd(
  calStart: string,
  calendarType: string,
  targetDate: string,
): string | null {
  const cadence = cadenceDays(calendarType);
  if (cadence === 0) return null;
  const start = new Date(`${calStart}T12:00:00Z`);
  const target = new Date(`${targetDate}T12:00:00Z`);
  const diffDays = Math.floor((target.getTime() - start.getTime()) / 86_400_000);
  const cycles = Math.floor(diffDays / cadence);
  const periodStart = addDays(calStart, cycles * cadence);
  return addDays(periodStart, cadence - 1);
}

export function PayrollPayRuns() {
  const queryClient = useQueryClient();
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [selectedCalendarId, setSelectedCalendarId] = useState<string>('');
  const [periodEndDate, setPeriodEndDate] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: status } = useQuery({
    queryKey: ['xero-status'],
    queryFn: () => api.xeroStatus(),
  });
  const tenants = useMemo(() => status?.tenants ?? [], [status]);

  useEffect(() => {
    if (!selectedTenantId && tenants.length > 0) {
      setSelectedTenantId(tenants[0].tenantId);
    }
  }, [tenants, selectedTenantId]);

  const { data: calendarsData, isLoading: calendarsLoading } = useQuery({
    queryKey: ['pay-calendars', selectedTenantId],
    queryFn: () => api.payrollPayCalendars(selectedTenantId),
    enabled: Boolean(selectedTenantId),
  });
  const calendars = calendarsData?.calendars ?? [];

  // Default the calendar selection.
  useEffect(() => {
    if (!selectedCalendarId && calendars.length > 0) {
      setSelectedCalendarId(calendars[0].payrollCalendarId);
    }
  }, [calendars, selectedCalendarId]);

  const selectedCalendar = useMemo(
    () => calendars.find((c) => c.payrollCalendarId === selectedCalendarId),
    [calendars, selectedCalendarId],
  );

  // Default the period end to the most-recent closed period whenever the
  // calendar changes.
  useEffect(() => {
    if (!selectedCalendar) return;
    const suggested = lastClosedPeriodEnd(
      selectedCalendar.startDate,
      selectedCalendar.calendarType,
    );
    if (suggested) setPeriodEndDate(suggested);
  }, [selectedCalendar]);

  const { data: payRunsData } = useQuery({
    queryKey: ['pay-runs', selectedTenantId],
    queryFn: () => api.payrollListPayRuns(selectedTenantId),
    enabled: Boolean(selectedTenantId),
  });
  const payRuns = payRunsData?.payRuns ?? [];

  // Live-derived period end the request will actually use — snapped to the
  // calendar boundary so the user sees what Xero will receive.
  const snappedPeriodEnd = useMemo(() => {
    if (!selectedCalendar || !periodEndDate) return periodEndDate;
    return (
      snapToPeriodEnd(selectedCalendar.startDate, selectedCalendar.calendarType, periodEndDate) ??
      periodEndDate
    );
  }, [selectedCalendar, periodEndDate]);

  const isSnapping = snappedPeriodEnd !== periodEndDate;

  async function handleCreate() {
    if (!selectedTenantId || !selectedCalendarId || !snappedPeriodEnd) return;
    setCreating(true);
    setError(null);
    try {
      await api.payrollCreatePayRun({
        tenantId: selectedTenantId,
        payrollCalendarId: selectedCalendarId,
        periodEndDate: snappedPeriodEnd,
      });
      queryClient.invalidateQueries({ queryKey: ['pay-runs', selectedTenantId] });
      // Lock the input to the snapped value so the field reflects what got
      // sent, not what the user typed.
      if (isSnapping) setPeriodEndDate(snappedPeriodEnd);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Pay run creation failed');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div
      style={{
        padding: 'var(--spacing-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-4)',
        maxWidth: 1200,
      }}
    >
      <div>
        <h1 style={{ margin: 0, marginBottom: 4 }}>Payroll · Pay runs</h1>
        <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
          <strong>Slice 5</strong> — create a DRAFT pay run in Xero for one of your pay calendars.
          Xero auto-generates payslips from the timesheets we pushed in{' '}
          <Link to="/payroll/timesheets" style={{ color: 'var(--color-accent)' }}>
            Timesheets
          </Link>{' '}
          plus each employee's pay template. Review and post in Xero.
        </div>
      </div>

      <div style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Create draft pay run</h2>

        {tenants.length === 0 ? (
          <div
            style={{
              padding: 'var(--spacing-4)',
              border: '1px dashed var(--color-border)',
              borderRadius: 'var(--radius-default)',
              textAlign: 'center',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-muted)',
            }}
          >
            No Xero connection.{' '}
            <Link to="/payroll/settings" style={{ color: 'var(--color-accent)' }}>
              Connect Xero
            </Link>{' '}
            first.
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: 'var(--spacing-3)',
              alignItems: 'flex-end',
            }}
          >
            {tenants.length > 1 && (
              <div>
                <label
                  style={{
                    display: 'block',
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    color: 'var(--color-text-muted)',
                    marginBottom: 4,
                  }}
                >
                  Xero organisation
                </label>
                <select
                  style={inputStyle}
                  value={selectedTenantId}
                  onChange={(e) => {
                    const v = e.currentTarget.value;
                    setSelectedTenantId(v);
                    setSelectedCalendarId('');
                  }}
                >
                  {tenants.map((t) => (
                    <option key={t.tenantId} value={t.tenantId}>
                      {t.tenantName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: 'var(--color-text-muted)',
                  marginBottom: 4,
                }}
              >
                Pay calendar
              </label>
              <select
                style={inputStyle}
                value={selectedCalendarId}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  setSelectedCalendarId(v);
                }}
                disabled={calendarsLoading || calendars.length === 0}
              >
                {calendarsLoading ? (
                  <option>Loading…</option>
                ) : calendars.length === 0 ? (
                  <option>No pay calendars</option>
                ) : (
                  calendars.map((c) => (
                    <option key={c.payrollCalendarId} value={c.payrollCalendarId}>
                      {c.name} ({c.calendarType})
                    </option>
                  ))
                )}
              </select>
            </div>

            <div>
              <label
                style={{
                  display: 'block',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  color: 'var(--color-text-muted)',
                  marginBottom: 4,
                }}
              >
                Period end date
              </label>
              <input
                type="date"
                style={inputStyle}
                value={periodEndDate}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  setPeriodEndDate(v);
                }}
              />
              {isSnapping && snappedPeriodEnd && (
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                    marginTop: 4,
                  }}
                >
                  Will snap to {snappedPeriodEnd} (calendar boundary)
                </div>
              )}
            </div>

            <div>
              <button
                style={buttonPrimary}
                onClick={handleCreate}
                disabled={creating || !selectedCalendarId || !periodEndDate}
              >
                {creating ? 'Creating…' : 'Create draft pay run'}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div
            style={{
              marginTop: 'var(--spacing-3)',
              padding: 'var(--spacing-2) var(--spacing-3)',
              backgroundColor: 'rgba(229, 62, 62, 0.1)',
              border: '1px solid var(--color-danger, #e53e3e)',
              borderRadius: 'var(--radius-default)',
              color: 'var(--color-danger, #e53e3e)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {error}
          </div>
        )}
      </div>

      <div style={cardStyle}>
        <h2 style={{ marginTop: 0 }}>Recent pay runs</h2>
        {payRuns.length === 0 ? (
          <div
            style={{
              padding: 'var(--spacing-3)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-muted)',
              fontStyle: 'italic',
            }}
          >
            No pay runs created yet for this organisation.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Period</th>
                <th style={thStyle}>Payment date</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Created</th>
                <th style={thStyle}>Xero</th>
              </tr>
            </thead>
            <tbody>
              {payRuns.map((p) => (
                <tr key={p.id}>
                  <td style={tdStyle}>
                    <Link
                      to={`/payroll/pay-runs/${p.id}`}
                      style={{ color: 'var(--color-accent)', textDecoration: 'none' }}
                    >
                      <strong>
                        {formatDate(p.periodStartDate)} → {formatDate(p.periodEndDate)}
                      </strong>
                    </Link>
                  </td>
                  <td style={tdStyle}>{p.paymentDate ? formatDate(p.paymentDate) : '—'}</td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 10px',
                        borderRadius: 999,
                        backgroundColor:
                          p.status === 'POSTED'
                            ? 'rgba(72, 187, 120, 0.15)'
                            : 'rgba(214, 147, 42, 0.15)',
                        color: p.status === 'POSTED' ? '#2f855a' : '#d6932a',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {p.status}
                    </span>
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    {p.createdByEmail ? `${p.createdByEmail} · ` : ''}
                    {new Date(p.updatedAt).toLocaleDateString('en-AU', {
                      day: 'numeric',
                      month: 'short',
                      timeZone: BRISBANE_TZ,
                    })}
                  </td>
                  <td
                    style={{
                      ...tdStyle,
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                      fontFamily: 'monospace',
                    }}
                  >
                    {p.xeroPayRunId.slice(0, 8)}…
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
